import React from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { AlertCircle, ArrowRight, Bot, Cpu } from 'lucide-react';
import { FallbackRequest } from '../services/aiService';

interface AIFallbackModalProps {
  request: FallbackRequest;
  onConfirm: () => void;
  onCancel: () => void;
}

export const AIFallbackModal: React.FC<AIFallbackModalProps> = ({ request, onConfirm, onCancel }) => {
  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-md z-[100] flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.9, y: 20 }}
        className="bg-zinc-900 border border-zinc-800 rounded-3xl w-full max-w-md overflow-hidden shadow-2xl"
      >
        <div className="p-8 text-center space-y-6">
          <div className="flex justify-center">
            <div className="relative">
              <div className="w-20 h-20 bg-red-500/10 rounded-full flex items-center justify-center border border-red-500/20">
                <AlertCircle className="text-red-500" size={40} />
              </div>
              <motion.div 
                animate={{ scale: [1, 1.2, 1], opacity: [0.5, 1, 0.5] }}
                transition={{ repeat: Infinity, duration: 2 }}
                className="absolute -top-2 -right-2 bg-zinc-900 p-1 rounded-full border border-zinc-800"
              >
                <Bot size={16} className="text-zinc-500" />
              </motion.div>
            </div>
          </div>

          <div className="space-y-2">
            <h2 className="text-2xl font-black text-white">Inconvenientes con {request.currentProviderName}</h2>
            <p className="text-zinc-400 text-sm leading-relaxed">
              El proveedor actual no pudo procesar tu solicitud. ¿Deseas intentar con <span className="text-indigo-400 font-bold">{request.nextProviderName}</span> para completar la tarea?
            </p>
          </div>

          <div className="flex items-center justify-center gap-4 py-4">
            <div className="flex flex-col items-center gap-2">
              <div className="w-12 h-12 bg-zinc-800 rounded-2xl flex items-center justify-center border border-zinc-700 grayscale opacity-50">
                <Cpu size={24} className="text-zinc-500" />
              </div>
              <span className="text-[10px] text-zinc-600 font-bold uppercase">{request.currentProviderName}</span>
            </div>
            
            <ArrowRight className="text-zinc-700" size={20} />

            <div className="flex flex-col items-center gap-2">
              <div className="w-12 h-12 bg-indigo-500/20 rounded-2xl flex items-center justify-center border border-indigo-500/30">
                <Cpu size={24} className="text-indigo-400" />
              </div>
              <span className="text-[10px] text-indigo-500 font-bold uppercase">{request.nextProviderName}</span>
            </div>
          </div>

          <div className="flex flex-col gap-3 pt-4">
            <button
              onClick={onConfirm}
              className="w-full py-4 bg-indigo-500 hover:bg-indigo-600 text-white rounded-2xl font-bold transition-all shadow-lg shadow-indigo-500/20 flex items-center justify-center gap-2"
            >
              Sí, probar con {request.nextProviderName}
              <ArrowRight size={18} />
            </button>
            <button
              onClick={onCancel}
              className="w-full py-4 bg-zinc-800 hover:bg-zinc-700 text-zinc-400 hover:text-white rounded-2xl font-bold transition-all"
            >
              No, cancelar proceso
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};
